import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { repairOrReplace } from "@/data/seo";
import { Wrench, Recycle, AlertTriangle } from "lucide-react";

/**
 * Rule-based, no-LLM repair-vs-replace decision tool.
 * Inputs: appliance age (years) + quoted repair cost (USD).
 */
export default function RepairVsReplace({ applianceSlug }) {
  const [age, setAge] = useState("");
  const [cost, setCost] = useState("");
  const [result, setResult] = useState(null);

  function calculate() {
    const a = parseFloat(age);
    const c = parseFloat(cost);
    if (isNaN(a) || isNaN(c) || a < 0 || c <= 0) return;
    setResult(repairOrReplace({ applianceSlug, age: a, repairCost: c }));
  }

  const Icon = result?.verdict === "Repair" ? Wrench : result?.verdict === "Replace" ? Recycle : AlertTriangle;

  return (
    <div className="bg-[#FBF6E8] text-slate-900 rounded-xl border-4 border-orange/80 p-6 sm:p-8 shadow-[0_0_60px_rgba(230,92,0,0.18)]">
      <div className="text-xs uppercase tracking-widest text-orange-700 font-semibold">Decision tool · No nonsense</div>
      <h3 className="display text-2xl sm:text-3xl font-semibold mt-2 tracking-tight">Repair or replace?</h3>
      <p className="text-slate-600 text-sm mt-1.5">Drop in the age and a quoted repair cost. Our rule-based guide gives an honest answer in 2 seconds.</p>
      <div className="grid sm:grid-cols-2 gap-3 mt-5">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-600 font-medium">Appliance age (years)</Label>
          <Input data-testid="rvr-age" className="mt-1.5 bg-white border-slate-300" type="number" value={age} onChange={(e) => setAge(e.target.value)} placeholder="e.g. 8" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-600 font-medium">Quoted repair cost ($)</Label>
          <Input data-testid="rvr-cost" className="mt-1.5 bg-white border-slate-300" type="number" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="e.g. 320" />
        </div>
      </div>
      <Button data-testid="rvr-calc" onClick={calculate} disabled={!age || !cost} className="mt-4 bg-orange hover:bg-[#ff7a1f] text-white rounded-md h-11 px-6 font-semibold tracking-wide">
        Get verdict
      </Button>

      {result && (
        <div className={`mt-5 rounded-lg p-5 border-2 ${result.color === "orange" ? "bg-orange/10 border-orange text-orange-900" : result.color === "gold" ? "bg-amber-50 border-amber-400 text-amber-900" : "bg-blue-50 border-blue-500 text-blue-900"}`}>
          <div className="flex items-start gap-3">
            <Icon className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <div className="display text-2xl font-semibold">{result.verdict}</div>
              <p className="text-sm mt-1.5">{result.reason}</p>
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                <div className="bg-white/60 rounded-md p-2">Life used: <b className="num">{(result.lifeRatio * 100).toFixed(0)}%</b></div>
                <div className="bg-white/60 rounded-md p-2">Repair vs new: <b className="num">{(result.costRatio * 100).toFixed(0)}%</b></div>
              </div>
            </div>
          </div>
        </div>
      )}
      <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
        Rule: if repair ≥ 60% of a new unit, replace. If 40–60% AND the appliance is past 60% of typical life, lean toward replace. Otherwise: repair pays off.
      </p>
    </div>
  );
}
